import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import {
    Info,
    BarChart3,
    Archive,
    Settings,
    ArrowLeft,
    Lock,
    Loader2,
    MapPin,
    House
} from 'lucide-react';
import InfoTab from './InfoTab';
import ReadingsTab from './ReadingsTab';
import ArchiveTab from './ArchiveTab';
import SettingsTab from './SettingsTab';

const Aksioma01Page = ({ device, onBack, isDesktop = false, onSelectDevice }) => {
    const [activeTab, setActiveTab] = useState('info');
    const [deviceData, setDeviceData] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isScrolled, setIsScrolled] = useState(false);
    const scrollRef = useRef(null);

    useEffect(() => {
        loadDeviceData();
    }, [device.name]);

    const handleScroll = () => {
        if (scrollRef.current) {
            setIsScrolled(scrollRef.current.scrollTop > 10);
        }
    };

    const loadDeviceData = async () => {
        setIsLoading(true);
        try {
            const token = localStorage.getItem('token');
            const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5022';

            const response = await axios.get(
                `${API_URL}/api/devices/aksioma01/${device.name}`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            setDeviceData(response.data);
        } catch (err) {
            console.error('Ошибка загрузки данных устройства:', err);
            setDeviceData(device);
        } finally {
            setIsLoading(false);
        }
    };

    const tabs = [
        { id: 'info', name: 'Информация', icon: Info },
        { id: 'readings', name: 'Показания', icon: BarChart3 },
        { id: 'archive', name: 'Архив', icon: Archive },
        { id: 'settings', name: 'Настройки', icon: Settings },
    ];

    const address = `${device.city}, ${device.street}, дом ${device.houseNumber}`;

    return (
        <div className="h-full flex flex-col">
            {/* Шапка */}
            <div className="bg-slate-800 border-b border-slate-700 p-4 sticky top-0 z-10">
                {/* Кнопка назад - только на мобильных */}
                {!isDesktop && (
                    <div className={`flex items-center gap-3 transition-all duration-300 overflow-hidden ${isScrolled ? 'max-h-0 mb-0 opacity-0' : 'max-h-20 mb-3 opacity-100'}`}>
                        <button
                            onClick={onBack}
                            className="flex items-center gap-2 px-3 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition text-sm"
                        >
                            <ArrowLeft className="w-4 h-4" />
                            К списку устройств
                        </button>
                    </div>
                )}

                {/* Заголовок устройства — скрывается при скролле */}
                <div className={`transition-all duration-300 overflow-hidden ${isScrolled ? 'max-h-0 opacity-0 mb-0' : 'max-h-60 opacity-100 mb-3'}`}>
                    <div className="flex items-center gap-2 flex-wrap mb-2">
                        <span className="px-2 py-1 bg-blue-600/20 text-blue-400 rounded text-xs font-semibold">
                            Аксиома-01
                        </span>
                        {device.isOplombirovan && (
                            <span className="px-2 py-1 bg-green-600/20 text-green-400 rounded text-xs font-semibold flex items-center gap-1">
                                <Lock className="w-3 h-3" />
                                Опломбирован {device.oplombirovanDate && new Date(device.oplombirovanDate).toLocaleDateString('ru-RU')}
                            </span>
                        )}
                    </div>
                    <h2 className="text-lg sm:text-xl font-bold text-white">
                        Тепловычислитель Аксиома-01 {device.name}
                    </h2>

                    {/* ✅ ОБНОВЛЕННЫЙ БЛОК АДРЕСА С КЛИКАБЕЛЬНЫМ MAP PIN */}
                    <div className="flex items-center gap-2 text-slate-400 text-sm mt-1">
                        <button
                            onClick={() => {
                                // 1. Сохраняем данные устройства как надежный источник (используем deviceData или fallback на device)
                                const targetDevice = deviceData || device;
                                localStorage.setItem('switchPage', 'map');
                                localStorage.setItem('selectedDeviceOnMap', JSON.stringify(targetDevice));

                                // 2. Уведомляем Dashboard о необходимости переключить страницу
                                window.dispatchEvent(new CustomEvent('switchPageRequest'));

                                // 3. Уведомляем MapPage с небольшой задержкой, чтобы React успел отрендерить карту
                                setTimeout(() => {
                                    window.dispatchEvent(new CustomEvent('selectDeviceOnMap', { detail: { device: targetDevice } }));
                                }, 100);
                            }}
                            className="p-1 -ml-1 hover:bg-slate-700 hover:text-blue-400 rounded transition flex items-center gap-1 group"
                            title="Показать на карте"
                        >
                            <MapPin className="w-4 h-4 flex-shrink-0 group-hover:text-blue-400" />
                            <span className="group-hover:text-white transition truncate max-w-[200px] sm:max-w-md">
                                {address}
                            </span>
                        </button>

                        {/* ✅ Кнопка перехода к дому */}
                        {onSelectDevice && device?.city && device?.street && device?.houseNumber && (
                            <button
                                onClick={() => {
                                    onSelectDevice({
                                        type: 'house',
                                        data: {
                                            city: device.city,
                                            street: device.street,
                                            house: `Дом ${device.houseNumber}`
                                        }
                                    });
                                }}
                                className="p-1 hover:bg-slate-700 hover:text-white rounded transition"
                                title="Перейти к дому"
                            >
                                <House className="w-4 h-4" />
                            </button>
                        )}
                    </div>

                    {device.description && (
                        <p className="text-slate-300 text-sm mt-1">{device.description}</p>
                    )}
                </div>

                {/* Вкладки */}
                <div className="flex gap-1 overflow-x-auto custom-scrollbar-x-auto pb-1">
                    {tabs.map(tab => {
                        const Icon = tab.icon;
                        return (
                            <button
                                key={tab.id}
                                onClick={() => setActiveTab(tab.id)}
                                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition flex-shrink-0 ${activeTab === tab.id
                                    ? 'bg-blue-600 text-white'
                                    : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                                    }`}
                            >
                                <Icon className="w-4 h-4" />
                                <span>{tab.name}</span>
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* Контент вкладок */}
            <div
                ref={scrollRef}
                onScroll={handleScroll}
                className="flex-1 overflow-y-auto custom-scrollbar p-4 sm:p-6"
            >
                {isLoading ? (
                    <div className="flex items-center justify-center py-12">
                        <div className="text-center">
                            <Loader2 className="animate-spin h-8 w-8 text-blue-500 mx-auto mb-3" />
                            <p className="text-slate-400 text-sm">Загрузка данных устройства...</p>
                        </div>
                    </div>
                ) : (
                    <>
                        {activeTab === 'info' && <InfoTab device={deviceData || device} />}
                        {activeTab === 'readings' && <ReadingsTab device={deviceData || device} />}
                        {activeTab === 'archive' && <ArchiveTab device={deviceData || device} />}
                        {activeTab === 'settings' && <SettingsTab device={deviceData || device} />}
                    </>
                )}
            </div>
        </div>
    );
};

export default Aksioma01Page;